import axios from 'axios';
import type { AxiosRequestConfig } from 'axios';

const API_BASE_URL =
  (import.meta.env.VITE_API_URL as string | undefined) || 'http://localhost:3000/api/v1';

export const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

const MUTATING = new Set(['post', 'put', 'patch', 'delete']);

// Add request interceptor
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('auth_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    // Invariant #25 — every mutating request must carry an X-Command-Id, or the API
    // rejects it with 400 VALIDATION_ERROR. This was attached ad-hoc at each call site,
    // so any new POST that forgot it broke at runtime (e.g. /discounts/override).
    // Attach a fresh id centrally; an explicit header from the caller still wins, so a
    // deliberate retry can reuse the same id to hit the idempotency cache.
    if (MUTATING.has((config.method ?? '').toLowerCase()) && !config.headers['X-Command-Id']) {
      config.headers['X-Command-Id'] = crypto.randomUUID();
    }
    return config;
  },
  (error) => Promise.reject(error)
);

/**
 * Silent re-authentication. Access tokens are deliberately short-lived (15 min, see
 * AuthService.ACCESS_TTL_SEC); the 7-day refresh token issued alongside them is what keeps
 * the session alive. It was being stored at login and never used, so every session died at
 * the 15-minute mark and hard-bounced to /login mid-work. On a 401 we now rotate once and
 * replay the original request; only a failed rotation ends the session.
 *
 * Rotation is single-flight: the refresh token is one-use (the server revokes it as it
 * issues the next), so if several requests 401 together they must all await ONE rotation.
 * Racing rotations would spend the token twice and the loser would log everybody out.
 */
let refreshInFlight: Promise<string> | null = null;

async function rotateSession(): Promise<string> {
  const refreshToken = localStorage.getItem('refresh_token');
  if (!refreshToken) throw new Error('no refresh token');
  const { data } = await apiClient.post<{
    data: { accessToken: string; refreshToken: string };
  }>('/auth/refresh', { refreshToken });
  localStorage.setItem('auth_token', data.data.accessToken);
  localStorage.setItem('refresh_token', data.data.refreshToken);
  return data.data.accessToken;
}

function refreshSession(): Promise<string> {
  refreshInFlight ??= rotateSession().finally(() => {
    refreshInFlight = null;
  });
  return refreshInFlight;
}

function endSession(): void {
  localStorage.removeItem('auth_token');
  localStorage.removeItem('refresh_token');
  window.location.href = '/login';
}

type RetriableConfig = AxiosRequestConfig & { _retried?: boolean };

// Add response interceptor
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    // A 401 from the auth endpoints themselves (e.g. wrong credentials on /auth/login, or a
    // refresh token that is spent/expired) is a form error the caller surfaces as a toast —
    // NOT a live session to rescue. Redirecting there would full-page reload the login screen
    // and swallow the error notification. Only refresh/bounce when an *authenticated* request
    // elsewhere comes back 401.
    const config = (error.config ?? {}) as RetriableConfig;
    const url = config.url ?? '';
    const isAuthEndpoint = url.includes('/auth/');
    if (error.response?.status === 401 && !isAuthEndpoint && !config._retried) {
      config._retried = true;
      try {
        const token = await refreshSession();
        // Overwrite the Authorization the request interceptor stamped with the now-stale
        // token. The X-Command-Id is reused deliberately: this request never reached the
        // handler, and an unchanged id keeps the replay idempotent (Invariant #25).
        config.headers = { ...config.headers, Authorization: `Bearer ${token}` };
        return await apiClient.request(config);
      } catch {
        endSession();
        return Promise.reject(error);
      }
    }
    if (error.response?.status === 401 && !isAuthEndpoint) {
      endSession();
    }
    // A must-change-password session is inert everywhere except /me/password. If any other
    // request comes back with the forced-change gate, send the user to set their password.
    const code = error.response?.data?.code;
    if (
      error.response?.status === 403 &&
      code === 'PASSWORD_CHANGE_REQUIRED' &&
      !window.location.pathname.startsWith('/change-password')
    ) {
      window.location.href = '/change-password';
    }
    return Promise.reject(error);
  }
);

export default apiClient;
